/**
 * Светлая и тёмная тема: что показывать.
 *
 * Состояний у настройки ТРИ, а тем две, и путать их нельзя. «Как в системе»
 * это не третья тема, а отказ выбирать: человек оставляет решение тому, что у
 * него стоит в телефоне или ноутбуке. Состояние это на месте и работает как
 * раньше, выбирается руками и умолчанием быть перестало.
 *
 * ⚠️ **Умолчание это «Светлая», решение владельца 17.09.2026.** До этого дня
 * умолчанием было «как в системе» (решение владельца 02.09.2026). Меняется
 * ровно одно: что видит человек, НИЧЕГО не выбиравший. Теперь он видит
 * светлую, что бы ни стояло у него в системе, а выбравший «как в системе»
 * получает систему, как и получал.
 *
 * Выбор живёт в браузере (localStorage), а не у нас на сервере, и это тоже
 * решение владельца: тема зависит от того, ГДЕ сидит человек, а не от того,
 * кто он. Один и тот же преподаватель за ноутбуком у окна и с телефона вечером
 * хочет разного, а ручка и миграция дали бы ему одно на оба места.
 *
 * ⚠️ **`data-theme` на `<html>` стоит ВСЕГДА, во всех трёх состояниях.** Это
 * устройство от 02.09.2026 в силе и переездом умолчания не тронуто: «как в
 * системе» разворачивается в конкретную тему ещё до первой отрисовки (скрипт
 * `public/boot-theme.js`), поэтому медиазапроса `prefers-color-scheme` в
 * `styles.css` нет ни одного, и светлая палитра объявлена там ровно один раз.
 * Разбор цены другого устройства стоит в шапке `styles.css`.
 *
 * ⚠️ Модуль чистый: ни React, ни браузерных API, ни DOM. Так его читает
 * `node:test` (бегунок берёт только `*.test.ts`), и так же его читает разбор
 * хранимого значения, которому надо отработать раньше первого рендера. Всё
 * браузерное живёт в `use-theme.tsx`.
 */

/** Что человек выбрал. Три состояния, и «как в системе» это одно из них. */
export type ThemeChoice = "system" | "light" | "dark";

/** Что в итоге показывается. Тем две, третьей не бывает. */
export type Appearance = "light" | "dark";

/**
 * Ключ в localStorage.
 *
 * ⚠️ Та же строка стоит вторым местом, в крошечном `public/boot-theme.js`: он
 * выставляет атрибут темы ДО первой отрисовки, а импортировать оттуда нечего
 * (отдельным файлом он не по вкусу, инлайн душит CSP). Расхождение этих двух
 * строк выглядело бы как вспышка не той темы на первом кадре, поэтому
 * `theme.test.ts` прогоняет тот файл целиком в подставном окружении и сверяет
 * выставленный атрибут с `parseTheme` и `appearanceOf`.
 */
export const THEME_KEY = "edu.theme";

/** Порядок здесь же и порядок кнопок в переключателе, слева направо. */
export const THEME_CHOICES: readonly ThemeChoice[] = ["system", "light", "dark"];

/**
 * Умолчание, оно же значение для всего непонятного из хранилища.
 *
 * Решение владельца 17.09.2026, разбор в шапке. Состояний по-прежнему три:
 * «как в системе» никуда не делось, просто достаётся выбором, а не молчанием.
 */
export const DEFAULT_THEME: ThemeChoice = "light";

/**
 * Хранимый выбор в значение.
 *
 * Отдельной функцией, а не сравнением по месту: значение приезжает из
 * localStorage, то есть из прошлых версий приложения и из чужих рук. Всё
 * непонятное это умолчание, то есть светлая, а не падение приложения.
 */
export function parseTheme(raw: string | null | undefined): ThemeChoice {
  return THEME_CHOICES.includes(raw as ThemeChoice) ? (raw as ThemeChoice) : DEFAULT_THEME;
}

/**
 * Выбор человека плюс то, что стоит у него в системе, дают тему.
 *
 * Единственное место, где эти два источника встречаются. Всё остальное
 * (Excalidraw, атрибут на корне, подписи) считается отсюда, поэтому доска и
 * страница разойтись не могут.
 */
export function appearanceOf(choice: ThemeChoice, system: Appearance): Appearance {
  return choice === "system" ? system : choice;
}

/**
 * Следить ли сейчас за системной темой.
 *
 * Довод это счётчик открытых экранов, попросивших тему не трогать. Сегодня
 * такой экран ровно один, комната урока, но счётчиком, а не флагом: `StrictMode`
 * прогоняет эффекты дважды, и пара «завести, снять» на флаге выключила бы
 * слежение навсегда после первого же урока.
 *
 * ⚠️ Правило комнаты живёт ЗДЕСЬ, отдельной функцией, потому что сломать его
 * тихо проще всего: у человека с автоматическим переходом на тёмную по закату
 * доска перекрасилась бы в середине занятия, а Excalidraw делает тёмную тему
 * фильтром инверсии по холсту, то есть перекрасился бы и уже нарисованный
 * штрих. Явное переключение человеком при этом работает и в комнате: оно
 * меняет не системную половину, а выбор.
 */
export function followsSystem(frozenScreens: number): boolean {
  return frozenScreens === 0;
}

/**
 * Подпись состояния на кнопке.
 *
 * Читает её и преподаватель, и шестиклассник, и родитель, поэтому ни одного
 * термина: «авто» и «системная тема» вслух не говорят, а «как в системе»
 * говорят. Что это значит, объясняет строка под кнопками в самом
 * переключателе, а не название.
 */
export function themeLabel(choice: ThemeChoice): string {
  if (choice === "light") return "Светлая";
  if (choice === "dark") return "Тёмная";
  return "Как в системе";
}
